import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  ScrollArea,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import type { CreateRoomBookingDTO } from "../../domain/contracts/room-bookings";
import { useCreateRoomBooking } from "../api/queries";
import { buildRoomBookingSuccessSearch } from "../lib/room-booking-success-search";
import { RoomBookingForm } from "./RoomBookingForm";

type CreateRoomBookingDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialDate?: string;
  initialStartTime?: string;
  initialEndTime?: string;
  onSuccess?: () => void;
};

function toTimeHHmm(s: string | undefined): string {
  if (!s) return "09:00";
  if (s.length >= 8) return s.slice(0, 5);
  return s;
}

export function CreateRoomBookingDialog({
  open,
  onOpenChange,
  initialDate,
  initialStartTime,
  initialEndTime,
  onSuccess,
}: CreateRoomBookingDialogProps) {
  const nav = useNavigate();
  const createBooking = useCreateRoomBooking();

  const initialValues = {
    date: initialDate ?? "",
    startTime: toTimeHHmm(initialStartTime) ?? "09:00",
    endTime: toTimeHHmm(initialEndTime) ?? "10:00",
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent
          className="!max-w-[min(95vw,1200px)] max-h-[90vh] overflow-hidden p-0"
          showCloseButton
        >
          <DialogHeader className="border-b px-6 py-4">
            <DialogTitle>ຈອງຫ້ອງ</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[calc(90vh-4rem)] overflow-auto px-6 pb-6">
            <RoomBookingForm
              key={
                open
                  ? `${initialDate ?? ""}-${initialStartTime ?? ""}-${initialEndTime ?? ""}`
                  : "closed"
              }
              mode="create"
              initialValues={initialValues}
              onSubmit={async (vals) => {
                try {
                  const payload = vals as CreateRoomBookingDTO;
                  const result = await createBooking.mutateAsync(payload);
                  onSuccess?.();
                  onOpenChange(false);
                  const successSearch = buildRoomBookingSuccessSearch(
                    result.id,
                    result,
                  );
                  if (successSearch) {
                    nav({
                      to: "/app/room-bookings/success",
                      search: successSearch,
                    });
                  }
                } catch {
                  // Error handled by mutation
                }
              }}
              submitting={createBooking.isPending}
            />
          </ScrollArea>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
